import { useEffect, useState } from 'react';
import Select from 'react-select';
import { logEntryApi, stationsApi } from '../api/logbookApi';
import './NewEntryModal.css';

const CATEGORIES = [
  { value: 'other',        label: 'Egyéb / parkoló', icon: 'ti-parking' },
  { value: 'arrival_stop', label: 'Érkezés',    icon: 'ti-map-pin' },
  { value: 'pickup',       label: 'Felrakó',    icon: 'ti-package' },
  { value: 'delivery',     label: 'Lerakó',     icon: 'ti-package-export' },
  { value: 'customs',      label: 'Vámkezelés', icon: 'ti-stamp' },
  { value: 'refuel',       label: 'Tankolás',   icon: 'ti-gas-station' },
];

const STATION_CATEGORIES = ['pickup', 'delivery', 'customs'];
const FREE_TEXT_CATEGORIES = ['other', 'refuel'];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#2bb344' : '#e1e8e2',
    borderRadius: 10,
    padding: '4px 2px',
    boxShadow: 'none',
    fontSize: 16,
    '&:hover': { borderColor: '#2bb344' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#ffffff',
    border: '1px solid #e1e8e2',
    borderRadius: 10,
    overflow: 'hidden',
    zIndex: 20,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e8f9eb' : state.isFocused ? '#f7faf7' : 'transparent',
    color: state.isSelected ? '#1f8c34' : '#1c241e',
    cursor: 'pointer',
    fontSize: 15,
  }),
  singleValue: (base) => ({ ...base, color: '#1c241e' }),
  input: (base) => ({ ...base, color: '#1c241e' }),
  placeholder: (base) => ({ ...base, color: '#8a958c' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#8a958c' }),
};

function nowLocal() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export default function NewEntryModal({ logbookId, onClose, onCreated }) {
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);
  const [stations, setStations] = useState([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [arrival, setArrival] = useState(nowLocal());
  const [departure, setDeparture] = useState('');
  const [km, setKm] = useState('');
  const [diesel, setDiesel] = useState('');
  const [adblue, setAdblue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const needsStation = STATION_CATEGORIES.includes(category);
  const isFreeText = FREE_TEXT_CATEGORIES.includes(category);

  useEffect(() => {
    if (needsStation) {
      setStationsLoading(true);
      stationsApi.list(category)
        .then(res => {
          setStations(res.data.map(s => ({
            value: s.id,
            label: `${s.city} – ${s.order_identifier}`,
            city: s.city,
          })));
        })
        .catch(() => setError('Nem sikerült betölteni az állomásokat.'))
        .finally(() => setStationsLoading(false));
    }
    setSelectedStation(null);
    setCity('');
  }, [category]);

  const handleSave = async () => {
    setError('');
    if (!category) { setError('Kategória választása kötelező.'); return; }
    if (!km) { setError('Km óra állás megadása kötelező.'); return; }
    if (needsStation && !selectedStation) { setError('Állomás választása kötelező.'); return; }
    if (isFreeText && !city.trim()) { setError('Város megadása kötelező.'); return; }
    if (category === 'refuel' && !diesel) { setError('Dízel mennyiség megadása kötelező.'); return; }

    const payload = {
      logbook: logbookId,
      category,
      km: parseInt(km),
      arrival: arrival || null,
      departure: departure || null,
      city: needsStation ? selectedStation.city : city.trim(),
      orderstation: needsStation ? selectedStation.value : null,
      diesel: diesel || null,
      adblue: adblue || null,
    };

    setSaving(true);
    try {
      await logEntryApi.create(payload);
      onCreated();
    } catch (err) {
      setError('Nem sikerült menteni a bejegyzést.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box entry-modal-box" onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <h3><i className="ti ti-plus"></i> Új bejegyzés</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="modal-body">

          {/* Kategória választó */}
          <label>Kategória</label>
          <div className="category-grid">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className={`category-btn ${category === cat.value ? 'selected' : ''}`}
                onClick={() => setCategory(cat.value)}
              >
                <i className={`ti ${cat.icon}`}></i>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Állomás dropdown (Felrakó/Lerakó/Vámkezelés) */}
          {needsStation && (
            <>
              <label>Állomás</label>
              <Select
                options={stations}
                value={selectedStation}
                onChange={setSelectedStation}
                isLoading={stationsLoading}
                placeholder="Keress állomásra..."
                noOptionsMessage={() => 'Nincs elérhető állomás'}
                styles={selectStyles}
              />
            </>
          )}

          {/* Szabad szöveges város (Indulás/Érkezés/Tankolás) */}
          {isFreeText && (
            <>
              <label>Város</label>
              <input
                type="text"
                className="entry-input"
                placeholder="pl. Budapest"
                value={city}
                onChange={e => setCity(e.target.value)}
              />
            </>
          )}

          {/* Km óra */}
          <label>Km óra állása</label>
          <input
            type="number"
            className="entry-input"
            placeholder="pl. 125430"
            value={km}
            onChange={e => setKm(e.target.value)}
          />

          {/* Idők */}
          <div className="entry-time-row">
            <div className="entry-time-col">
              <label>Érkezés</label>
              <input
                type="datetime-local"
                className="entry-input"
                value={arrival}
                onChange={e => setArrival(e.target.value)}
              />
            </div>
            <div className="entry-time-col">
              <label>Távozás</label>
              <input
                type="datetime-local"
                className="entry-input"
                value={departure}
                onChange={e => setDeparture(e.target.value)}
              />
            </div>
          </div>

          {/* Tankolás mezők */}
          {category === 'refuel' && (
            <div className="entry-time-row">
              <div className="entry-time-col">
                <label>Dízel (liter)</label>
                <input
                  type="number"
                  className="entry-input"
                  placeholder="pl. 320"
                  value={diesel}
                  onChange={e => setDiesel(e.target.value)}
                />
              </div>
              <div className="entry-time-col">
                <label>AdBlue (liter)</label>
                <input
                  type="number"
                  className="entry-input"
                  placeholder="pl. 20"
                  value={adblue}
                  onChange={e => setAdblue(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && <p className="modal-error">{error}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Mégse</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Bejegyzés mentése'}
          </button>
        </div>
      </div>
    </div>
  );
}